import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  Fab,
  Badge,
  Alert
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  Title,
  ExpandMore,
  Sort,
  Visibility,
  VisibilityOff,
  Refresh,
  Star,
  ContentCopy,
  Upload,
  Download
} from '@mui/icons-material';
import { adminAchievementService } from '../../../services/adminAchievementService';

const RARITY_OPTIONS = [
  { value: 'common', label: 'Common', color: '#9E9E9E' },
  { value: 'uncommon', label: 'Uncommon', color: '#4CAF50' },
  { value: 'rare', label: 'Rare', color: '#2196F3' },
  { value: 'epic', label: 'Epic', color: '#9C27B0' },
  { value: 'legendary', label: 'Legendary', color: '#FF9800' }
];

const TITLE_TEMPLATES = [
  {
    type: 'NEWCOMER',
    name: 'Newcomer',
    description: 'Welcome to HypeCircle!',
    color: '#9E9E9E',
    rarity: 'common',
    minAchievements: 0,
    minPoints: 0,
    isDefault: true
  },
  {
    type: 'MOVIE_BUFF',
    name: 'Movie Buff',
    description: 'Passionate about movies',
    color: '#2196F3',
    rarity: 'uncommon',
    minAchievements: 5,
    minPoints: 100
  },
  {
    type: 'CRITIC',
    name: 'Critic',
    description: 'Expert movie reviewer',
    color: '#9C27B0',
    rarity: 'rare',
    minAchievements: 10,
    minPoints: 250
  },
  {
    type: 'LEGEND',
    name: 'Legend',
    description: 'HypeCircle legend',
    color: '#FF9800',
    rarity: 'legendary',
    minAchievements: 25,
    minPoints: 1000
  }
];

const EnhancedTitleManager = () => {
  const [titles, setTitles] = useState([]);
  const [filteredTitles, setFilteredTitles] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterRarity, setFilterRarity] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    description: '',
    color: '#1976d2',
    rarity: 'common',
    minAchievements: 1,
    minPoints: 0,
    requiredAchievements: [],
    isActive: true,
    isDefault: false
  });

  useEffect(() => {
    loadTitles();
    loadAchievements();
  }, []);

  useEffect(() => {
    filterAndSortTitles();
  }, [titles, searchQuery, filterRarity, filterStatus, sortBy, sortOrder]);

  const loadTitles = async () => {
    setLoading(true);
    try {
      const data = await adminAchievementService.getAllTitles();
      setTitles(data);
    } catch (error) {
      console.error('Error loading titles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAchievements = async () => {
    try {
      const data = await adminAchievementService.getAllAchievements();
      setAchievements(data);
    } catch (error) {
      console.error('Error loading achievements:', error);
    }
  };

  const filterAndSortTitles = () => {
    let filtered = [...titles];

    if (searchQuery) {
      filtered = filtered.filter(title => 
        title.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        title.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        title.type.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filterRarity !== 'ALL') {
      filtered = filtered.filter(title => title.rarity === filterRarity);
    }

    if (filterStatus !== 'ALL') {
      const isActive = filterStatus === 'ACTIVE';
      filtered = filtered.filter(title => title.isActive === isActive);
    }

    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredTitles(filtered);
  };

  const groupedTitles = () => {
    const groups = {};
    filteredTitles.forEach(title => {
      const rarity = title.rarity || 'common';
      if (!groups[rarity]) {
        groups[rarity] = [];
      }
      groups[rarity].push(title);
    });
    return groups;
  };

  const handleBulkAction = async (action) => {
    if (selectedTitles.length === 0) return;
    
    setLoading(true);
    try {
      switch (action) {
        case 'activate':
          await Promise.all(selectedTitles.map(id => 
            adminAchievementService.updateTitle(id, { isActive: true })
          ));
          break;
        case 'deactivate':
          await Promise.all(selectedTitles.map(id => 
            adminAchievementService.updateTitle(id, { isActive: false })
          ));
          break;
        case 'delete':
          if (window.confirm(`Delete ${selectedTitles.length} titles?`)) {
            await Promise.all(selectedTitles.map(id => 
              adminAchievementService.deleteTitle(id)
            ));
          }
          break;
      }
      setSelectedTitles([]);
      await loadTitles();
    } catch (error) {
      console.error('Bulk action error:', error);
    } finally {
      setLoading(false);
      setBulkMenuAnchor(null);
    }
  };

  const handleSelectTitle = (id) => {
    setSelectedTitles(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleSave = async () => {
    try {
      if (editingTitle) {
        await adminAchievementService.updateTitle(editingTitle.id, formData);
      } else {
        await adminAchievementService.createTitle(formData);
      }
      await loadTitles();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving title:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this title?')) {
      try {
        await adminAchievementService.deleteTitle(id);
        await loadTitles();
      } catch (error) {
        console.error('Error deleting title:', error);
      }
    }
  };

  const handleEdit = (title) => {
    setEditingTitle(title);
    setFormData({
      ...title,
      requiredAchievements: title.requiredAchievements || []
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTitle(null);
    setFormData({
      type: '',
      name: '',
      description: '',
      color: '#1976d2',
      rarity: 'common',
      minAchievements: 1,
      minPoints: 0,
      requiredAchievements: [],
      isActive: true,
      isDefault: false
    });
  };

  const handleCreateFromTemplate = async (template) => {
    try {
      await adminAchievementService.createTitle(template);
      await loadTitles();
      setTemplateDialogOpen(false);
    } catch (error) {
      console.error('Error creating title from template:', error);
    }
  };

  const handleBulkCreateTemplates = async () => {
    setLoading(true);
    try {
      await Promise.all(TITLE_TEMPLATES.map(template => 
        adminAchievementService.createTitle(template)
      ));
      await loadTitles();
      setTemplateDialogOpen(false);
    } catch (error) {
      console.error('Error creating templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRarityColor = (rarity) => {
    return RARITY_OPTIONS.find(r => r.value === rarity)?.color || '#9E9E9E';
  };

  const rarityGroups = groupedTitles();

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Title Management</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => setTemplateDialogOpen(true)}
          >
            Templates
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadTitles}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
          >
            Create Title
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Rarity</InputLabel>
                <Select
                  value={filterRarity}
                  onChange={(e) => setFilterRarity(e.target.value)}
                >
                  <MenuItem value="ALL">All Rarities</MenuItem>
                  {RARITY_OPTIONS.map(rarity => (
                    <MenuItem key={rarity.value} value={rarity.value}>{rarity.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="rarity">Rarity</MenuItem>
                  <MenuItem value="minPoints">Min Points</MenuItem>
                  <MenuItem value="createdAt">Created</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <IconButton
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                <Sort sx={{ transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'none' }} />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedTitles.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent sx={{ py: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">
                {selectedTitles.length} title(s) selected
              </Typography>
              <Box>
                <Button
                  size="small"
                  onClick={() => setBulkMenuAnchor(document.getElementById('bulk-menu-button'))}
                  id="bulk-menu-button"
                  sx={{ color: 'inherit' }}
                >
                  Bulk Actions
                </Button>
                <Menu
                  anchorEl={bulkMenuAnchor}
                  open={Boolean(bulkMenuAnchor)}
                  onClose={() => setBulkMenuAnchor(null)}
                >
                  <MenuItem onClick={() => handleBulkAction('activate')}>
                    <ListItemIcon><Visibility /></ListItemIcon>
                    <ListItemText>Activate</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleBulkAction('deactivate')}>
                    <ListItemIcon><VisibilityOff /></ListItemIcon>
                    <ListItemText>Deactivate</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => handleBulkAction('delete')} sx={{ color: 'error.main' }}>
                    <ListItemIcon><Delete color="error" /></ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                  </MenuItem>
                </Menu>
                <Button
                  size="small"
                  onClick={() => setSelectedTitles([])}
                  sx={{ color: 'inherit', ml: 1 }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Grouped View */}
      <Box>
        {Object.entries(rarityGroups).map(([rarity, rarityTitles]) => (
          <Accordion key={rarity} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  width={16}
                  height={16}
                  borderRadius="50%"
                  bgcolor={getRarityColor(rarity)}
                />
                <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                  {rarity}
                </Typography>
                <Badge badgeContent={rarityTitles.length} color="primary" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedTitles.length > 0 && selectedTitles.length < rarityTitles.length}
                          checked={rarityTitles.length > 0 && rarityTitles.every(t => selectedTitles.includes(t.id))}
                          onChange={() => {
                            const rarityIds = rarityTitles.map(t => t.id);
                            const allSelected = rarityIds.every(id => selectedTitles.includes(id));
                            if (allSelected) {
                              setSelectedTitles(prev => prev.filter(id => !rarityIds.includes(id)));
                            } else {
                              setSelectedTitles(prev => [...new Set([...prev, ...rarityIds])]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Requirements</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rarityTitles.map((title) => (
                      <TableRow key={title.id} selected={selectedTitles.includes(title.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedTitles.includes(title.id)}
                            onChange={() => handleSelectTitle(title.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Title sx={{ color: title.color }} />
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: title.color }}>
                                {title.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {title.description}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={title.type} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {title.minAchievements} achievements, {title.minPoints} points
                            {title.requiredAchievements?.length > 0 && (
                              <>
                                <br />
                                + {title.requiredAchievements.length} specific
                              </>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Chip
                              label={title.isActive ? 'Active' : 'Inactive'}
                              color={title.isActive ? 'success' : 'default'}
                              size="small"
                            />
                            {title.isDefault && (
                              <Chip label="Default" color="primary" size="small" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleEdit(title)} size="small">
                            <Edit />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(title.id)} size="small">
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setDialogOpen(true)}
      >
        <Add />
      </Fab>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTitle ? 'Edit Title' : 'Create Title'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Type"
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                helperText="Unique identifier (e.g., NEWCOMER, EXPERT)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                helperText="Display name for the title"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Rarity</InputLabel>
                <Select
                  value={formData.rarity}
                  onChange={(e) => setFormData(prev => ({ ...prev, rarity: e.target.value }))}
                >
                  {RARITY_OPTIONS.map(rarity => (
                    <MenuItem key={rarity.value} value={rarity.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          width={12}
                          height={12}
                          borderRadius="50%"
                          bgcolor={rarity.color}
                        />
                        {rarity.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Color"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box display="flex" alignItems="center" gap={2} p={2} bgcolor="grey.100" borderRadius={1}>
                <Typography variant="caption">Preview:</Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: formData.color,
                    fontWeight: 'bold',
                    textShadow: '0 0 2px rgba(0,0,0,0.3)'
                  }}
                >
                  {formData.name || 'Title Name'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Min Achievements"
                type="number"
                value={formData.minAchievements}
                onChange={(e) => setFormData(prev => ({ ...prev, minAchievements: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Min Points"
                type="number"
                value={formData.minPoints}
                onChange={(e) => setFormData(prev => ({ ...prev, minPoints: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={achievements}
                getOptionLabel={(achievement) => achievement.name}
                value={achievements.filter(a => formData.requiredAchievements.includes(a.type))}
                onChange={(_, newValue) => setFormData(prev => ({
                  ...prev,
                  requiredAchievements: newValue.map(a => a.type)
                }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Required Achievements"
                    helperText="Specific achievements required for this title"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDefault}
                    onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                  />
                }
                label="Default Title"
                helperText="New users start with this title"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" startIcon={<Save />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Title Templates</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Create common title templates quickly. These are pre-configured titles for typical user progression.
          </Alert>
          <Grid container spacing={2}>
            {TITLE_TEMPLATES.map((template, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box
                        width={12}
                        height={12}
                        borderRadius="50%"
                        bgcolor={getRarityColor(template.rarity)}
                      />
                      <Typography variant="h6" sx={{ color: template.color }}>
                        {template.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      {template.description}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Type: {template.type}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Requirements: {template.minAchievements} achievements, {template.minPoints} points
                    </Typography>
                    <Typography variant="caption" display="block">
                      Rarity: {template.rarity}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleCreateFromTemplate(template)}
                      sx={{ mt: 1 }}
                    >
                      Create
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>
            Close
          </Button>
          <Button onClick={handleBulkCreateTemplates} variant="contained" disabled={loading}>
            Create All Templates
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedTitleManager;